import {
  Box,
  Button,
  HStack,
  Input,
  NativeSelect,
  Text,
  VStack,
} from '@chakra-ui/react'
import { memo, useEffect, useMemo, useState } from 'react'

import { Checkbox } from '@mangovoice/mango-ui'

import { useExtensionsI18n } from '../i18n/provider'
import type { LineFilterOption } from '../types/api.types'
import type { LineStatusFilterValue } from '../utils/extensions'
import { SideSheetDialog } from './SideSheetDialog'

interface ExtensionsListFilterValue {
  statusFilter: LineStatusFilterValue | 'all'
  assignedUserFilter: number[]
  locationFilter: number[]
}

interface ExtensionsListFiltersSideSheetProps {
  isOpen: boolean
  value: ExtensionsListFilterValue
  users: LineFilterOption[]
  locations: LineFilterOption[]
  /**
   * Whether to show the Active/Inactive/All status filter. Non-staff users
   * cannot see inactive extensions, so the status filter should be hidden
   * from them (matches legacy admin which only renders the active-state UI
   * inside `is_mango_user` blocks).
   */
  showStatusFilter?: boolean
  onChange: (next: ExtensionsListFilterValue) => void
  onClear: () => void
  onCancel: () => void
  onApply: () => void
}

export const ExtensionsListFiltersSideSheet =
  memo<ExtensionsListFiltersSideSheetProps>(
    ({
      isOpen,
      value,
      users,
      locations,
      showStatusFilter = true,
      onChange,
      onClear,
      onCancel,
      onApply,
    }) => {
      const { text } = useExtensionsI18n()
      const [assignedUserSearch, setAssignedUserSearch] = useState('')
      const [locationSearch, setLocationSearch] = useState('')

      useEffect(() => {
        if (!isOpen) return
        setAssignedUserSearch('')
        setLocationSearch('')
      }, [isOpen])

      const toggleSelection = (selected: number[], id: number) => {
        if (selected.includes(id)) {
          return selected.filter((value) => value !== id)
        }
        return [...selected, id]
      }

      const filteredUsers = useMemo(() => {
        const query = assignedUserSearch.trim().toLowerCase()
        if (!query) return users
        return users.filter((user) => user.name.toLowerCase().includes(query))
      }, [assignedUserSearch, users])

      const filteredLocations = useMemo(() => {
        const query = locationSearch.trim().toLowerCase()
        if (!query) return locations
        return locations.filter((location) =>
          location.name.toLowerCase().includes(query),
        )
      }, [locationSearch, locations])

      return (
        <SideSheetDialog
          isOpen={isOpen}
          onClose={onCancel}
          title={text('extensions.filters.title', 'Filter')}
          body={
            <VStack align="stretch" gap={4}>
              {showStatusFilter && (
                <VStack align="stretch" gap={2}>
                  <Text fontSize="lg" fontWeight="semibold" color="fg.default">
                    {text('extensions.filters.status', 'Status')}
                  </Text>
                  <NativeSelect.Root size="md">
                    <NativeSelect.Field
                      value={value.statusFilter}
                      bg="bg.panel"
                      borderColor="border.default"
                      onChange={(e) =>
                        onChange({
                          ...value,
                          statusFilter: e.target.value as
                            LineStatusFilterValue | 'all',
                        })
                      }
                    >
                      <option value="active">
                        {text('extensions.filters.active', 'Active')}
                      </option>
                      <option value="inactive">
                        {text('extensions.filters.inactive', 'Inactive')}
                      </option>
                      <option value="all">
                        {text('extensions.filters.all', 'All')}
                      </option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </VStack>
              )}

              <Box
                borderWidth="1px"
                borderColor="border.default"
                borderRadius="lg"
                bg="bg.panel"
                boxShadow="sm"
                p={4}
              >
                <VStack align="stretch" gap={3}>
                  <Text fontSize="2xl" fontWeight="semibold" color="fg.default">
                    {text('extensions.filters.assignedUser', 'Assigned User')}
                  </Text>
                  <Input
                    size="md"
                    value={assignedUserSearch}
                    onChange={(event) =>
                      setAssignedUserSearch(event.target.value)
                    }
                    placeholder={text(
                      'extensions.filters.assignedUserPlaceholder',
                      'Select members...',
                    )}
                    bg="bg.subtle"
                    borderColor="border.default"
                    _placeholder={{ color: 'fg.subtle' }}
                  />
                  <VStack
                    align="stretch"
                    gap={2}
                    maxH="240px"
                    overflowY="auto"
                    borderWidth="1px"
                    borderColor="border.default"
                    borderRadius="md"
                    p={2}
                    bg="bg.panel"
                  >
                    {filteredUsers.map((user) => {
                      const checked = value.assignedUserFilter.includes(user.id)
                      return (
                        <Checkbox
                          key={user.id}
                          checked={checked}
                          onCheckedChange={() =>
                            onChange({
                              ...value,
                              assignedUserFilter: toggleSelection(
                                value.assignedUserFilter,
                                user.id,
                              ),
                            })
                          }
                        >
                          <Text
                            as="span"
                            color="fg.default"
                            fontWeight="medium"
                          >
                            {user.name}
                          </Text>
                        </Checkbox>
                      )
                    })}
                    {filteredUsers.length === 0 && (
                      <Text px={2} py={1} fontSize="sm" color="fg.subtle">
                        {text(
                          'extensions.filters.noMatches',
                          'No matches found',
                        )}
                      </Text>
                    )}
                  </VStack>
                </VStack>
              </Box>

              <Box
                borderWidth="1px"
                borderColor="border.default"
                borderRadius="lg"
                bg="bg.panel"
                boxShadow="sm"
                p={4}
              >
                <VStack align="stretch" gap={3}>
                  <Text fontSize="2xl" fontWeight="semibold" color="fg.default">
                    {text('extensions.filters.location', 'Location')}
                  </Text>
                  <Input
                    size="md"
                    value={locationSearch}
                    onChange={(event) => setLocationSearch(event.target.value)}
                    placeholder={text(
                      'extensions.filters.locationPlaceholder',
                      'Select Locations...',
                    )}
                    bg="bg.subtle"
                    borderColor="border.default"
                    _placeholder={{ color: 'fg.subtle' }}
                  />
                  <VStack
                    align="stretch"
                    gap={2}
                    maxH="240px"
                    overflowY="auto"
                    borderWidth="1px"
                    borderColor="border.default"
                    borderRadius="md"
                    p={2}
                    bg="bg.panel"
                  >
                    {filteredLocations.map((location) => {
                      const checked = value.locationFilter.includes(location.id)
                      return (
                        <Checkbox
                          key={location.id}
                          checked={checked}
                          onCheckedChange={() =>
                            onChange({
                              ...value,
                              locationFilter: toggleSelection(
                                value.locationFilter,
                                location.id,
                              ),
                            })
                          }
                        >
                          <Text
                            as="span"
                            color="fg.default"
                            fontWeight="medium"
                          >
                            {location.name}
                          </Text>
                        </Checkbox>
                      )
                    })}
                    {filteredLocations.length === 0 && (
                      <Text px={2} py={1} fontSize="sm" color="fg.subtle">
                        {text(
                          'extensions.filters.noMatches',
                          'No matches found',
                        )}
                      </Text>
                    )}
                  </VStack>
                </VStack>
              </Box>
            </VStack>
          }
          footer={
            <HStack gap={3} w="full" justify="space-between">
              <Button variant="ghost" colorPalette="neutral" onClick={onClear}>
                {text('extensions.filters.clearFilters', 'Clear filters')}
              </Button>
              <Button variant="ghost" colorPalette="neutral" onClick={onCancel}>
                {text('extensions.common.cancel', 'Cancel')}
              </Button>
              <Button colorPalette="brand" onClick={onApply}>
                {text('extensions.filters.apply', 'Apply')}
              </Button>
            </HStack>
          }
        />
      )
    },
  )

ExtensionsListFiltersSideSheet.displayName = 'ExtensionsListFiltersSideSheet'
